<template>
    <!-- Something the profile can't have yet: what it needs, and the way to get it. The button sits
         beside the words, and goes under them when there isn't room. -->
    <div class="upgrade-prompt">
        <span v-if="icon" class="upgrade-prompt__icon" aria-hidden="true">
            <svg v-if="icon === 'plus'" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2.4" stroke-linecap="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="8.5" />
                <path d="M12 7.5V12l3 2" />
            </svg>
        </span>

        <div class="upgrade-prompt__body">
            <p v-if="title || $slots.title" class="upgrade-prompt__title"><slot name="title">{{ title }}</slot></p>
            <p v-if="$slots.default" class="upgrade-prompt__text"><slot /></p>
        </div>

        <component :is="to ? 'router-link' : href ? 'a' : 'button'" v-if="action" v-bind="linkAttrs"
            class="ldna-btn upgrade-prompt__btn" @click="!to && !href && emit('action')">
            {{ action }}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
            </svg>
        </component>
    </div>
</template>

<script setup>
import { computed, defineEmits, defineProps } from "vue";

const props = defineProps({
    title: { type: String, default: null },
    // The button's words, and where it goes: a route, a link outside the portal, or neither for a
    // button that emits "action"
    action: { type: String, default: null },
    to: { type: [Object, String], default: null },
    href: { type: String, default: null },
    // "plus" for something to add, "clock" for something on its way; null for none
    icon: { type: String, default: "plus" },
});

const emit = defineEmits(["action"]);

const linkAttrs = computed(() => {
    if (props.to) return { to: props.to };
    if (props.href) return { href: props.href, target: "_blank", rel: "noopener" };
    return { type: "button" };
});
</script>

<style lang="scss" scoped>
// A light take on the profile card's purple
.upgrade-prompt {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    border-radius: 14px;
    background: linear-gradient(135deg, #F3F0FF 0%, #F5F0FC 100%);
    border: 1px solid #E4DCFB;
    text-align: left;
}

.upgrade-prompt__icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    background: linear-gradient(160.67deg, #6a4bf9 4.13%, #c36ae3 100%);
}

.upgrade-prompt__body {
    flex: 1 1 160px;
    min-width: 0;
}

.upgrade-prompt__title {
    margin: 0 0 2px;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.4;
    color: #1A1F36;
}

.upgrade-prompt__text {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    color: #4A5472;

    :deep(strong) { color: #1A1F36; }
    :deep(a) { font-weight: 600; }
}

// Sized to its words, rather than .ldna-btn's full width; full width once it wraps on a phone
.upgrade-prompt__btn.upgrade-prompt__btn.upgrade-prompt__btn {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: auto;
    margin: 0 0 0 auto;
    padding: 10px 18px;
    white-space: nowrap;
}

@media (max-width: 575px) {
    .upgrade-prompt { align-items: flex-start; }

    .upgrade-prompt__btn.upgrade-prompt__btn.upgrade-prompt__btn {
        flex-basis: 100%;
        justify-content: center;
    }
}
</style>
